import { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import { stamp } from '@mds/shared/lib/dates';
import type { AdminContentPage } from '@mds/shared/types/content';

import { useSession } from '@/auth/use-session';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import GalleryPicker from '@/components/files/gallery-picker';
import Field, { inputCls } from '@/components/ui/field';
import { SegmentedToggle } from '@/components/ui/toolbar';
import * as content from '@/content/content.api';
import {
  LANGUAGE_LABELS,
  MAX_SECTIONS,
  blankSection,
  documentOf,
  draftOf,
  isDirty,
  policySchema,
  type PolicyDraft,
  type PolicyLanguage,
  type PolicyTextDraft,
  type SectionDraft,
} from '@/domain/booking-policy';
import { invalidAssets, isVersionConflict, statusLabel, statusTone } from '@/domain/cafe';
import { type FieldBinder, useValidation } from '@/lib/validation';

const SLUG = 'booking-policy' as const;

type Notice = { tone: 'ok' | 'warn' | 'bad'; text: string };

const TONES: Record<Notice['tone'], string> = {
  ok: 'border-brand-edge/35 bg-brand-wash text-brand-strong',
  warn: 'border-warn-edge bg-warn-wash text-warn-text',
  bad: 'border-danger-edge bg-danger-wash text-danger-text',
};

/**
 * The booking policy every visitor accepts before booking, in English and
 * Arabic, with an optional downloadable PDF.
 *
 * It is a managed content page like the café: Save writes a private draft and
 * Publish makes the saved draft live, as separate permissions and buttons.
 * Until a version is published the website keeps its bundled copy, and the API
 * refuses to publish one without a section in both languages.
 */
export default function BookingPolicyPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('content.manage');
  const mayPublish = can('content.publish');

  const page = useQuery({ queryKey: ['content', SLUG], queryFn: () => content.getPage(SLUG) });

  const [language, setLanguage] = useState<PolicyLanguage>('en');
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [seededEtag, setSeededEtag] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const validation = useValidation(policySchema, draft);

  const dirty = draft !== null && page.data ? isDirty(draft, page.data.document) : false;

  // Seeded from each newly loaded copy, unless the form holds unsaved edits —
  // a reload after a conflict must not wipe out what the editor typed.
  if (page.data && page.data.etag !== seededEtag && !dirty) {
    setSeededEtag(page.data.etag);
    setDraft(draftOf(page.data.document));
    validation.reset();
  }

  const adopt = (next: AdminContentPage<typeof SLUG>) => {
    queryClient.setQueryData(['content', SLUG], next);
    setSeededEtag(next.etag);
    setDraft(draftOf(next.document));
    validation.reset();
  };

  const run = async (
    action: (etag: string) => Promise<AdminContentPage<typeof SLUG>>,
    done: string,
    fallback: string,
  ) => {
    if (!page.data) return;
    setBusy(true);
    setNotice(null);
    try {
      adopt(await action(page.data.etag));
      setNotice({ tone: 'ok', text: done });
    } catch (e) {
      if (isVersionConflict(e)) {
        await page.refetch();
        setNotice({
          tone: 'warn',
          text: 'Someone else changed the booking policy while you were editing. Your edits are still in the form. Save again to keep yours, or discard to take theirs.',
        });
      } else {
        setNotice({
          tone: 'bad',
          text:
            invalidAssets(e).length > 0
              ? 'The PDF is no longer a public, ready file (deleted, made private, or still uploading). Pick another and try again.'
              : e instanceof ApiError
                ? e.message
                : fallback,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  /** Opens the language holding the first problem, so the focus `check()` moves lands on screen. */
  const checked = () => {
    const first = Object.keys(validation.errors)[0];
    if (first?.startsWith('ar')) setLanguage('ar');
    else if (first?.startsWith('en')) setLanguage('en');
    return validation.check();
  };

  const save = () => {
    if (!draft || !checked()) return;
    const document = documentOf(draft);
    void run(
      (etag) => content.savePage(SLUG, document, etag),
      'Draft saved. The website has not changed. Publish when it is ready.',
      'That did not save. Try again.',
    );
  };

  const discard = () => {
    if (!page.data) return;
    setDraft(draftOf(page.data.document));
    setSeededEtag(page.data.etag);
    setNotice(null);
    validation.reset();
  };

  if (page.isPending)
    return <p className="m-0 text-base text-ink-muted">Loading the booking policy…</p>;
  if (page.isError || !draft)
    return (
      <EmptyState
        title="Could not load the booking policy"
        hint={
          page.error instanceof ApiError ? page.error.message : 'The server could not be reached.'
        }
      />
    );

  const saved = page.data;
  const readOnly = !mayManage;
  const text = draft[language];
  const setText = (next: PolicyTextDraft) => setDraft({ ...draft, [language]: next });
  return (
    <div className="flex flex-col gap-6">
      {/* ------------------------------------------------ status and actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hairline bg-surface px-5 py-4 shadow-card">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Chip className={'border ' + statusTone(saved.status)}>
              {statusLabel(saved.status)}
            </Chip>
            {dirty && (
              <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
                Unsaved edits
              </Chip>
            )}
          </div>
          <span className="text-base text-ink-muted">
            Draft is revision {saved.draftRevisionNumber}
            {saved.publishedRevisionNumber !== null
              ? ` · revision ${saved.publishedRevisionNumber} is live since ${stamp(saved.publishedAt)}`
              : ' · nothing is live yet, so the website shows its bundled copy'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mayManage && dirty && (
            <Button variant="quiet" disabled={busy} onClick={discard}>
              Discard edits
            </Button>
          )}
          {mayManage && (
            <Button disabled={busy || !dirty} onClick={save}>
              {busy ? 'Working…' : 'Save draft'}
            </Button>
          )}
          {mayPublish && saved.status !== 'published' && (
            <Button
              variant="mint"
              disabled={busy || dirty}
              onClick={() => {
                if (!checked()) return;
                void run(
                  (etag) => content.publishPage(SLUG, etag),
                  `Revision ${saved.draftRevisionNumber} is live. Visitors now accept this policy.`,
                  'That did not publish. Try again.',
                );
              }}
            >
              Publish
            </Button>
          )}
          {mayPublish && saved.status !== 'draft' && (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() =>
                void run(
                  (etag) => content.unpublishPage(SLUG, etag),
                  'Unpublished. The website is back on its bundled copy; the draft and its history are kept.',
                  'That did not unpublish. Try again.',
                )
              }
            >
              Unpublish
            </Button>
          )}
        </div>
      </div>

      {mayPublish && dirty && saved.status !== 'published' && (
        <p className="m-0 text-base text-ink-muted">
          Save the draft first: publishing makes the saved draft live, not the form.
        </p>
      )}
      {!mayManage && (
        <p className="m-0 text-base text-ink-muted">
          You can read the booking policy but not change it.
        </p>
      )}
      {notice && (
        <p
          role={notice.tone === 'ok' ? 'status' : 'alert'}
          className={'m-0 rounded-xl border px-4 py-3 text-base ' + TONES[notice.tone]}
        >
          {notice.text}
        </p>
      )}

      {/* ------------------------------------------------------------- text */}
      <Card
        title="Policy text"
        action={
          <SegmentedToggle
            value={language}
            options={[
              { value: 'en', label: LANGUAGE_LABELS.en },
              { value: 'ar', label: LANGUAGE_LABELS.ar, lang: 'ar' },
            ]}
            onChange={setLanguage}
          />
        }
      >
        <PolicyTextEditor
          key={language}
          language={language}
          value={text}
          onChange={setText}
          readOnly={readOnly}
          field={validation.field}
        />
      </Card>

      {/* --------------------------------------------------------- download */}
      <Card title="Downloadable copy">
        {readOnly ? (
          <p className="m-0 text-base">
            {saved.assets.find((a) => a.fileId === draft.pdfFileId)?.originalFilename ??
              'No download.'}
          </p>
        ) : (
          <GalleryPicker
            kind="pdf"
            label="PDF"
            max={1}
            optional
            value={draft.pdfFileId ? [draft.pdfFileId] : []}
            onChange={(ids) => setDraft({ ...draft, pdfFileId: ids[0] ?? null })}
            known={saved.assets}
          />
        )}
        <p className="m-0 mt-3 text-base text-ink-muted">
          Optional. Visitors get a Download PDF link in the policy. Only public PDFs are listed;
          upload one on the Files page first.
        </p>
      </Card>
    </div>
  );
}

/** One language's title and sections. Arabic is typed right to left. */
function PolicyTextEditor({
  language,
  value,
  onChange,
  readOnly,
  field,
}: {
  language: PolicyLanguage;
  value: PolicyTextDraft;
  onChange: (next: PolicyTextDraft) => void;
  readOnly: boolean;
  field: FieldBinder;
}) {
  const dir = language === 'ar' ? 'rtl' : 'ltr';
  const setSection = (index: number, patch: Partial<SectionDraft>) =>
    onChange({
      ...value,
      sections: value.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    });
  const move = (index: number, by: -1 | 1) => {
    const sections = [...value.sections];
    const [section] = sections.splice(index, 1);
    sections.splice(index + by, 0, section!);
    onChange({ ...value, sections });
  };

  return (
    <div className="flex flex-col gap-5">
      <Field label="Title" required {...field(`${language}.title`)}>
        <input
          className={inputCls}
          dir={dir}
          lang={language}
          value={value.title}
          maxLength={160}
          disabled={readOnly}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
        />
      </Field>

      {value.sections.length === 0 && (
        <p className="m-0 text-base text-ink-muted">
          No sections yet. The policy needs at least one in each language before it can be
          published.
        </p>
      )}

      <ol className="m-0 flex list-none flex-col gap-4 p-0">
        {value.sections.map((section, i) => (
          <li
            key={section.key}
            className="flex flex-col gap-3 rounded-xl border border-hairline p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
                Section {i + 1}
              </span>
              {!readOnly && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="quiet"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    aria-label={`Move section ${i + 1} up`}
                  >
                    Up
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={i === value.sections.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label={`Move section ${i + 1} down`}
                  >
                    Down
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() =>
                      onChange({ ...value, sections: value.sections.filter((_, at) => at !== i) })
                    }
                    aria-label={`Remove section ${i + 1}`}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
            <Field label="Heading" required {...field(`${language}.sections.${i}.heading`)}>
              <input
                className={inputCls}
                dir={dir}
                lang={language}
                value={section.heading}
                maxLength={200}
                disabled={readOnly}
                onChange={(e) => setSection(i, { heading: e.target.value })}
              />
            </Field>
            <Field
              label="Text"
              hint="Plain text. Blank lines start a new paragraph."
              required
              {...field(`${language}.sections.${i}.body`)}
            >
              <textarea
                rows={5}
                className={inputCls}
                dir={dir}
                lang={language}
                value={section.body}
                maxLength={20000}
                disabled={readOnly}
                onChange={(e) => setSection(i, { body: e.target.value })}
              />
            </Field>
          </li>
        ))}
      </ol>

      {!readOnly && (
        <div>
          <Button
            variant="quiet"
            disabled={value.sections.length >= MAX_SECTIONS}
            onClick={() => onChange({ ...value, sections: [...value.sections, blankSection()] })}
          >
            Add section
          </Button>
        </div>
      )}
    </div>
  );
}
